import MARY from '../companies/mary/company';
import MIMESA from '../companies/mimesa/company';
import { REMOTE_TOGGLES, SCREEN_FEATURES } from './features';
import { getFeatureOverride } from './featureOverrides';

/**
 * Static registry of the companies this admin serves, mirroring the app's
 * (shelf-app/app/shared/company/companies.js).
 *
 * Each company lives in its own folder under `src/companies/<id>/`. This registry only wires
 * their light profiles (`company.js`: name, API URL, labels, features, navigation, access);
 * pages are wired in `./pages.js`. Shared code never compares company ids: it asks the
 * session company's config through `./session.js`, `./labels.js` and `./pages.js`.
 */

export const DEFAULT_COMPANY_ID = MARY.id;

export const COMPANIES = Object.freeze({
    [MARY.id]: MARY,
    [MIMESA.id]: MIMESA,
});

/**
 * @param {string|null|undefined} companyId
 * @returns {Object|null} The company config, or null for an unknown id
 */
export function getCompanyConfig(companyId) {
    if (typeof companyId !== 'string') return null;
    return Object.prototype.hasOwnProperty.call(COMPANIES, companyId) ? COMPANIES[companyId] : null;
}

export function isKnownCompany(companyId) {
    return getCompanyConfig(companyId) !== null;
}

/** The default company (Mary): what the admin serves without a session. */
export function getDefaultCompany() {
    return COMPANIES[DEFAULT_COMPANY_ID];
}

/**
 * Trim and drop trailing slashes. Only http(s) URLs are accepted.
 * @param {any} url
 * @returns {string|null}
 */
export function normalizeApiUrl(url) {
    if (typeof url !== 'string') return null;
    const value = url.trim().replace(/\/+$/, '');
    if (!/^https?:\/\/[^\s/]+/i.test(value)) return null;
    return value;
}

/**
 * `normalizeApiUrl`, but only https (or http to this machine): the bar for a URL that came from
 * storage or central-service rather than from the build.
 * @param {any} url
 * @returns {string|null}
 */
export function secureApiUrl(url) {
    const value = normalizeApiUrl(url);
    if (!value) return null;
    if (/^https:\/\//i.test(value)) return value;
    return /^http:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(value) ? value : null;
}

/**
 * A company's API URL when the session does not carry one: its build-time override
 * (REACT_APP_API_URL for Mary) or its production URL.
 * @param {Object} company
 * @returns {string}
 */
export function companyDefaultApiUrl(company) {
    return normalizeApiUrl(company?.apiUrlOverride) || normalizeApiUrl(company?.defaultApiUrl);
}

/** Mary's API URL: `REACT_APP_API_URL || https://service.shelfve.com`. */
export function getDefaultApiUrl() {
    return companyDefaultApiUrl(getDefaultCompany());
}

/**
 * Whether `companyId` has `feature`: its built-in value, changed by central-service as described
 * in ./features.js. Unknown companies and unknown features are off.
 * @param {string|null} companyId
 * @param {string} feature - See FEATURES in ./features.js
 * @returns {boolean}
 */
export function companyHasFeature(companyId, feature) {
    const company = getCompanyConfig(companyId);
    if (!company?.features || !Object.prototype.hasOwnProperty.call(company.features, feature)) return false;
    const builtIn = company.features[feature] === true;
    const remote = getFeatureOverride(company.id, feature);
    if (remote === undefined) return builtIn;
    return REMOTE_TOGGLES.includes(feature) ? remote : builtIn && remote;
}

/**
 * Whether `companyId`'s navigation includes the screen (nav key) `screen` and the screen's
 * feature (SCREEN_FEATURES) is on.
 * @param {string|null} companyId
 * @param {string} screen
 * @returns {boolean}
 */
export function companyHasScreen(companyId, screen) {
    const company = getCompanyConfig(companyId);
    if (!company) return false;
    const feature = SCREEN_FEATURES[screen];
    if (feature && !companyHasFeature(companyId, feature)) return false;
    const screens = company.navigation?.screens;
    if (screens === 'all') return true;
    return Array.isArray(screens) && screens.includes(screen);
}
